import React from 'react';
import { Shield } from 'lucide-react';
import { PageHeader, PageHero, PageFooter } from '../components/PageLayout';
import useSEO from '../hooks/useSEO';

const PrivacyPage = () => {
  useSEO({
    title: 'Confidentialité - Politique de Protection des Données | DJ Nomad',
    description: 'Politique de confidentialité DJ Nomad. Comment nous protégeons vos données.',
    canonical: 'https://djnomad.app/privacy',
    robots: 'noindex, follow'
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <PageHeader />

      {/* Hero */}
      <PageHero 
        icon={Shield}
        title="Politique de Confidentialité"
        subtitle="Dernière mise à jour : 17 décembre 2024"
      />

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12 flex-1">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-8">
            <p className="text-violet-700 font-semibold">
              🔒 Votre vie privée est notre priorité. DJ Nomad collecte le minimum de données nécessaires pour fournir un service de qualité.
            </p>
          </div>

          <div className="prose max-w-none space-y-8 text-gray-600">
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="leading-relaxed">
                La présente Politique de Confidentialité décrit comment DJ Nomad ("nous", "notre") collecte, utilise, stocke et protège vos données personnelles lorsque vous utilisez notre application web ("l'Application").
              </p>
              <p className="leading-relaxed">
                Conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés, nous nous engageons à protéger votre vie privée et vos données personnelles.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Responsable du Traitement</h2>
              <p className="leading-relaxed">
                Le responsable du traitement des données est DJ Nomad, joignable à l'adresse : privacy@djnomad.app
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Données Collectées</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.1 Données d'inscription</h3>
              <p className="leading-relaxed">Lors de la création de votre compte, nous collectons :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Adresse email (obligatoire)</li>
                <li>Mot de passe (hashé et crypté)</li>
                <li>Nom d'utilisateur (optionnel)</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.2 Données d'utilisation</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Événements créés (nom, date, lieu, statut)</li>
                <li>Playlists et bibliothèque musicale</li>
                <li>Métadonnées des fichiers audio (titre, artiste, BPM, durée)</li>
                <li>Fichiers audio uploadés (MP3, WAV, etc.)</li>
                <li>Préférences utilisateur et paramètres</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.3 Données techniques</h3>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Adresse IP</li>
                <li>Type de navigateur et version</li>
                <li>Système d'exploitation</li>
                <li>Horodatage des connexions</li>
                <li>Cookies techniques (authentification, sessions)</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">3.4 Données en mode offline</h3>
              <p className="leading-relaxed">
                En mode offline, vos données sont stockées localement sur votre appareil via IndexedDB et Service Workers. Ces données restent sous votre contrôle et ne sont pas transmises à nos serveurs tant que vous n'êtes pas connecté à Internet.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Finalités du Traitement</h2>
              <p className="leading-relaxed">Nous utilisons vos données pour :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Créer et gérer votre compte utilisateur</li>
                <li>Fournir les fonctionnalités de l'Application (dual-deck, playlists, mode offline)</li>
                <li>Synchroniser vos données entre appareils</li>
                <li>Améliorer l'Application et corriger les bugs</li>
                <li>Assurer la sécurité et prévenir les fraudes</li>
                <li>Respecter nos obligations légales</li>
                <li>Communiquer avec vous (support, mises à jour)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Base Légale du Traitement</h2>
              <p className="leading-relaxed">Le traitement de vos données repose sur :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-900">Consentement</strong> : pour l'utilisation de cookies non essentiels</li>
                <li><strong className="text-gray-900">Exécution du contrat</strong> : pour la fourniture du service</li>
                <li><strong className="text-gray-900">Intérêt légitime</strong> : pour améliorer l'Application et assurer la sécurité</li>
                <li><strong className="text-gray-900">Obligation légale</strong> : pour respecter les lois applicables</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Partage des Données</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">6.1 Principe général</h3>
              <p className="leading-relaxed">
                Nous ne vendons, ne louons ni ne partageons vos données personnelles avec des tiers à des fins commerciales.
              </p>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">6.2 Partage limité</h3>
              <p className="leading-relaxed">Vos données peuvent être partagées avec :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-900">Hébergement</strong> : MongoDB Atlas, Cloudflare R2 (stockage cloud sécurisé)</li>
                <li><strong className="text-gray-900">Services techniques</strong> : Fournisseurs de CDN et infrastructure</li>
                <li><strong className="text-gray-900">Autorités</strong> : En cas d'obligation légale ou de demande judiciaire</li>
              </ul>
              
              <p className="leading-relaxed mt-4">
                Tous nos partenaires sont soumis à des obligations strictes de confidentialité et de sécurité conformément au RGPD.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Transferts Internationaux</h2>
              <p className="leading-relaxed">
                Vos données sont stockées sur des serveurs situés au sein de l'Union Européenne. En cas de transfert hors UE, nous garantissons un niveau de protection adéquat via des clauses contractuelles types approuvées par la Commission Européenne.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Durée de Conservation</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-900">Compte actif</strong> : Tant que votre compte est actif</li>
                <li><strong className="text-gray-900">Compte inactif</strong> : 3 ans après la dernière connexion, puis suppression automatique</li>
                <li><strong className="text-gray-900">Données de facturation</strong> : 10 ans (obligation légale)</li>
                <li><strong className="text-gray-900">Logs techniques</strong> : 12 mois maximum</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Sécurité des Données</h2>
              <p className="leading-relaxed">Nous mettons en œuvre des mesures techniques et organisationnelles pour protéger vos données :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Chiffrement SSL/TLS pour toutes les communications</li>
                <li>Mots de passe hashés avec bcrypt</li>
                <li>Cookies HttpOnly pour prévenir les attaques XSS</li>
                <li>Protection contre les injections SQL et NoSQL</li>
                <li>Rate limiting pour prévenir les abus</li>
                <li>Sauvegardes régulières et chiffrées</li>
                <li>Accès restreint aux données (principe du moindre privilège)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Vos Droits</h2>
              <p className="leading-relaxed">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-900">Droit d'accès</strong> : Obtenir une copie de vos données</li>
                <li><strong className="text-gray-900">Droit de rectification</strong> : Corriger vos données inexactes</li>
                <li><strong className="text-gray-900">Droit à l'effacement</strong> : Supprimer vos données ("droit à l'oubli")</li>
                <li><strong className="text-gray-900">Droit à la limitation</strong> : Limiter le traitement de vos données</li>
                <li><strong className="text-gray-900">Droit à la portabilité</strong> : Recevoir vos données dans un format structuré</li>
                <li><strong className="text-gray-900">Droit d'opposition</strong> : S'opposer au traitement de vos données</li>
                <li><strong className="text-gray-900">Droit de retrait du consentement</strong> : Retirer votre consentement à tout moment</li>
              </ul>
              
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mt-4">
                <p className="font-semibold text-gray-900 mb-2">Comment exercer vos droits ?</p>
                <p>Contactez-nous à : <a href="mailto:privacy@djnomad.app" className="text-violet-600 hover:underline">privacy@djnomad.app</a></p>
                <p className="text-sm mt-2">Réponse sous 1 mois maximum (extensible à 3 mois si complexité)</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Cookies</h2>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">11.1 Types de cookies</h3>
              <p className="leading-relaxed">Nous utilisons :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-gray-900">Cookies essentiels</strong> : Authentification, sessions (obligatoires)</li>
                <li><strong className="text-gray-900">Cookies de performance</strong> : Statistiques d'utilisation (avec consentement)</li>
                <li><strong className="text-gray-900">Cookies de préférences</strong> : Paramètres utilisateur</li>
              </ul>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-3 mt-4">11.2 Gestion des cookies</h3>
              <p className="leading-relaxed">
                Vous pouvez gérer vos préférences de cookies via les paramètres de votre navigateur ou notre bandeau de consentement.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Mineurs</h2>
              <p className="leading-relaxed">
                L'Application est réservée aux personnes de 13 ans et plus. Les mineurs de moins de 18 ans doivent obtenir le consentement de leurs parents. Nous ne collectons pas sciemment de données d'enfants de moins de 13 ans.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Modifications</h2>
              <p className="leading-relaxed">
                Nous pouvons modifier cette Politique de Confidentialité à tout moment. Les modifications importantes vous seront notifiées par email ou via l'Application. La date de "Dernière mise à jour" en haut de cette page indique la version en vigueur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Contact & Réclamations</h2>
              <p className="leading-relaxed">
                Pour toute question sur cette politique ou pour exercer vos droits :
              </p>
              <ul className="list-none space-y-2 ml-4">
                <li>Email : <a href="mailto:privacy@djnomad.app" className="text-violet-600 hover:underline">privacy@djnomad.app</a></li>
                <li>Formulaire : <a href="/contact" className="text-violet-600 hover:underline">djnomad.app/contact</a></li>
              </ul>
              
              <p className="leading-relaxed mt-4">
                Vous pouvez également introduire une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) :
              </p>
              <ul className="list-none space-y-2 ml-4">
                <li>Site web : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">www.cnil.fr</a></li>
                <li>Adresse : 3 Place de Fontenoy - TSA 80715 - 75334 PARIS CEDEX 07</li>
              </ul>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PageFooter />
    </div>
  );
};

export default PrivacyPage;